/**
 * Vendor directory — company vendors with multi-contact support.
 * All roles can browse; Admins can create/edit/delete.
 */
import { useEffect, useState } from 'react';
import {
  Building2,
  Globe,
  Mail,
  Phone,
  Plus,
  Pencil,
  Search,
  Trash2,
} from 'lucide-react';
import {
  useVendors,
  useCreateVendor,
  useUpdateVendor,
  useDeleteVendor,
} from '../api.js';
import {
  ContactListEditor,
  newContactDraft,
  type ContactDraft,
} from '../../contacts/ContactListEditor.js';
import { useAuthStore } from '../../../store/auth.js';
import { Button } from '../../../shell/Button.js';
import { Badge } from '../../../shell/Badge.js';
import { Modal } from '../../../shell/Modal.js';
import { Input, Select } from '../../../shell/Field.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError } from '../../../lib/api.js';
import { VENDOR_CATEGORY } from '@shared/enums/index.js';
import type { Vendor } from '@shared/types';

function draftsFromVendor(vendor: Vendor | null): {
  emails: ContactDraft[];
  phones: ContactDraft[];
} {
  if (!vendor) return { emails: [], phones: [] };
  return {
    emails: vendor.contacts
      .filter((c) => c.kind === 'email')
      .map((c) =>
        newContactDraft('email', {
          label: c.label,
          value: c.value,
          isPrimary: c.isPrimary,
          personName: c.personName ?? undefined,
        }),
      ),
    phones: vendor.contacts
      .filter((c) => c.kind === 'phone')
      .map((c) =>
        newContactDraft('phone', {
          label: c.label,
          value: c.value,
          isPrimary: c.isPrimary,
          personName: c.personName ?? undefined,
        }),
      ),
  };
}

function VendorFormModal({
  open,
  onClose,
  vendor,
}: {
  open: boolean;
  onClose: () => void;
  vendor: Vendor | null;
}) {
  const create = useCreateVendor();
  const update = useUpdateVendor();
  const isEdit = Boolean(vendor);

  const [name, setName] = useState('');
  const [category, setCategory] = useState<string>('Other');
  const [status, setStatus] = useState<'Active' | 'Inactive'>('Active');
  const [website, setWebsite] = useState('');
  const [notes, setNotes] = useState('');
  const [emails, setEmails] = useState<ContactDraft[]>([]);
  const [phones, setPhones] = useState<ContactDraft[]>([]);

  useEffect(() => {
    if (!open) return;
    if (vendor) {
      setName(vendor.name);
      setCategory(vendor.category);
      setStatus(vendor.status);
      setWebsite(vendor.website ?? '');
      setNotes(vendor.notes ?? '');
      const d = draftsFromVendor(vendor);
      setEmails(d.emails);
      setPhones(d.phones);
    } else {
      setName('');
      setCategory('Other');
      setStatus('Active');
      setWebsite('');
      setNotes('');
      setEmails([]);
      setPhones([]);
    }
  }, [open, vendor]);

  const pending = create.isPending || update.isPending;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast('error', 'Vendor name is required.');
      return;
    }
    const contacts = [
      ...emails
        .filter((c) => c.value.trim())
        .map((c) => ({
          kind: 'email' as const,
          label: c.label,
          value: c.value.trim(),
          isPrimary: c.isPrimary,
          personName: c.personName?.trim() || null,
        })),
      ...phones
        .filter((c) => c.value.trim())
        .map((c) => ({
          kind: 'phone' as const,
          label: c.label,
          value: c.value.trim(),
          isPrimary: c.isPrimary,
          personName: c.personName?.trim() || null,
        })),
    ];
    const body = {
      name: name.trim(),
      category: category.trim() || 'Other',
      status,
      website: website.trim() || null,
      notes: notes.trim() || null,
      contacts,
    };
    try {
      if (isEdit && vendor) {
        await update.mutateAsync({ id: vendor.id, body });
        toast('success', 'Vendor updated.');
      } else {
        await create.mutateAsync(body);
        toast('success', 'Vendor created.');
      }
      onClose();
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to save vendor.';
      toast('error', msg);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Edit Vendor' : 'Add Vendor'} size="lg">
      <form onSubmit={onSubmit} className="space-y-4">
        <Input label="Company name" value={name} onChange={(e) => setName(e.target.value)} required />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {VENDOR_CATEGORY.map((c) => (
              <option key={c} value={c}>
                {c === 'ProfessionalServices' ? 'Professional Services' : c}
              </option>
            ))}
          </Select>
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as 'Active' | 'Inactive')}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </Select>
        </div>
        <Input
          label="Website (optional)"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://"
        />
        <Input
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Account numbers, SLAs, etc."
        />
        <div className="space-y-4 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Contacts</p>
          <ContactListEditor kind="email" items={emails} onChange={setEmails} showPersonName />
          <ContactListEditor kind="phone" items={phones} onChange={setPhones} showPersonName />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={pending}>{isEdit ? 'Save' : 'Create'}</Button>
        </div>
      </form>
    </Modal>
  );
}

export function VendorsPage() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'Admin';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('Active');
  const { data, isLoading } = useVendors({
    search: search.trim() || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Vendor | null>(null);
  const deleteVendor = useDeleteVendor();

  const items = data?.items ?? [];

  const handleDelete = async (vendor: Vendor) => {
    if (!window.confirm(`Delete vendor ${vendor.name}?`)) return;
    try {
      await deleteVendor.mutateAsync(vendor.id);
      toast('success', 'Vendor deleted.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to delete.';
      toast('error', msg);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Vendor directory</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Partners and suppliers — contacts in one place.
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => {
              setEditTarget(null);
              setModalOpen(true);
            }}
          >
            <Plus className="h-4 w-4" /> Add Vendor
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[16rem] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search vendors or contacts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="all">All</option>
        </select>
      </div>

      {isLoading ? (
        <p className="py-12 text-center text-sm text-slate-400">Loading vendors…</p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Building2 className="h-8 w-8" />}
          title="No vendors yet"
          description={
            isAdmin
              ? 'Add your first vendor to build the directory.'
              : 'Vendors will appear here once an admin adds them.'
          }
          action={
            isAdmin ? (
              <Button
                onClick={() => {
                  setEditTarget(null);
                  setModalOpen(true);
                }}
              >
                <Plus className="h-4 w-4" /> Add Vendor
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {items.map((vendor) => {
            const emails = vendor.contacts.filter((c) => c.kind === 'email');
            const phones = vendor.contacts.filter((c) => c.kind === 'phone');
            return (
              <article
                key={vendor.id}
                className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-semibold text-slate-900 dark:text-slate-100">{vendor.name}</h2>
                      <Badge tone={vendor.status === 'Active' ? 'green' : 'slate'}>{vendor.status}</Badge>
                      <Badge tone="blue">
                        {vendor.category === 'ProfessionalServices'
                          ? 'Professional Services'
                          : vendor.category}
                      </Badge>
                    </div>
                    {vendor.website && (
                      <a
                        href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-sm text-sky-600 hover:underline dark:text-sky-400"
                      >
                        <Globe className="h-3.5 w-3.5" />
                        {vendor.website.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                    {vendor.notes && (
                      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{vendor.notes}</p>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex shrink-0 gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditTarget(vendor);
                          setModalOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(vendor)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {(emails.length > 0 || phones.length > 0) && (
                  <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3 dark:border-slate-800">
                    {emails.map((c) => (
                      <a
                        key={c.id}
                        href={`mailto:${c.value}`}
                        className="flex items-center gap-2 text-sm text-slate-700 hover:text-sky-600 dark:text-slate-300"
                      >
                        <Mail className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">
                          {c.personName ? `${c.personName} · ` : ''}{c.value}
                        </span>
                      </a>
                    ))}
                    {phones.map((c) => (
                      <a
                        key={c.id}
                        href={`tel:${c.value}`}
                        className="flex items-center gap-2 text-sm text-slate-700 hover:text-sky-600 dark:text-slate-300"
                      >
                        <Phone className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">
                          {c.personName ? `${c.personName} · ` : ''}{c.value}
                        </span>
                      </a>
                    ))}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      <VendorFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditTarget(null);
        }}
        vendor={editTarget}
      />
    </div>
  );
}
